import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface GenerateParams {
  campaignId: string;
  senderId?: string;
  queueItemIds?: string[];
  onProgress?: (processed: number, total: number) => void;
}

export function useGenerateCampaignEmails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, senderId, queueItemIds, onProgress }: GenerateParams) => {
      let offset = 0;
      const batchSize = 10;
      let totalGenerated = 0;
      let totalFailed = 0;
      let mode = 'unknown';
      let total = 0;

      while (true) {
        const body: Record<string, any> = { campaign_id: campaignId, sender_id: senderId, offset, batch_size: batchSize };
        if (queueItemIds && queueItemIds.length > 0) {
          body.queue_item_ids = queueItemIds;
        }
        const { data, error } = await supabase.functions.invoke('generate-campaign-emails', {
          body,
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        totalGenerated += data?.generated || 0;
        totalFailed += data?.failed || 0;
        mode = data?.mode || mode;
        total = data?.total || total;

        onProgress?.(data?.nextOffset ?? total, total);

        if (data?.done) break;
        offset = data?.nextOffset ?? (offset + batchSize);
      }

      return { generated: totalGenerated, failed: totalFailed, mode, total };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaign-recipients-inline'] });
      toast.success(`AI emails generated: ${data?.generated || 0} succeeded, ${data?.failed || 0} failed`);
    },
    onError: (e: any) => toast.error(`Generation failed: ${e.message}`),
  });
}

interface VerifyParams {
  campaignId: string;
  reverify?: boolean;
  onProgress?: (processed: number, total: number) => void;
}

export function useVerifyCampaignEmails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, reverify, onProgress }: VerifyParams) => {
      let totalPassed = 0;
      let totalFlagged = 0;
      let firstReason: string | undefined;
      let firstResponseTotal = 0;
      let isFirst = true;

      while (true) {
        // Only send `reverify` on the FIRST call — the edge function clears
        // existing statuses on that initial invocation, then subsequent calls
        // just process the (now NULL) batch.
        const body: Record<string, any> = { campaign_id: campaignId };
        if (isFirst && reverify) body.reverify = true;

        const { data, error } = await supabase.functions.invoke('verify-campaign-emails', {
          body,
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        if (isFirst) {
          firstReason = data?.reason;
          firstResponseTotal = data?.total || 0;
          isFirst = false;
        }

        totalPassed += data?.passed || 0;
        totalFlagged += data?.flagged || 0;

        onProgress?.(data?.processed || 0, data?.total || 0);

        if (data?.done) break;
      }

      return { passed: totalPassed, flagged: totalFlagged, reason: firstReason, total: firstResponseTotal };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaign-recipients-inline'] });

      if (data?.reason === 'no_pending_items') {
        toast.info('No emails to verify — this campaign has no pending recipients.');
        return;
      }
      if (data?.reason === 'already_verified') {
        toast.info(
          `All ${data.total} emails are already verified. Use "Re-verify All" to run verification again.`,
          { duration: 6000 }
        );
        return;
      }

      const msg = data?.flagged > 0
        ? `Verification complete: ${data.passed} passed, ${data.flagged} flagged`
        : `Verification complete: All ${data.passed} emails passed`;
      toast.success(msg);
    },
    onError: (e: any) => toast.error(`Verification failed: ${e.message}`),
  });
}
